import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  PHONE_TIME_ZONES, TEMPLATE_KEY_FEATURES, TEMPLATE_STARTS, presetKeys,
  type HandsetModel, type PhoneTimeZone, type ProvisioningTemplate, type TemplateKeyKind, type TemplateStart,
} from '@arcnet/contracts';
import {
  useCreateTemplate, useDeleteTemplate, useDuplicateTemplate, useSaveTemplate, useTemplates,
  type DuplicatedTemplate,
} from '../../lib/api/hooks-templates.js';
import { useHandsetCatalogue } from '../../lib/api/hooks-provisioning.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useSubroute } from '../../lib/subroute.js';
import { relativeTime } from '../../lib/format.js';
import { Empty, ErrorBanner, SectionHead, Segmented, SubHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { FormActions, Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import {
  EMPTY_KEY, FEATURE_LABEL, KIND_LABEL, KeySettings, PhoneKeysEditor,
  derivedLabel, errorsBySlot, keysFromPreset, keysFromTemplate, keysToInput, slotOf,
  type KeyDraft, type KeyMap, type Seat,
} from './TemplateKeys.js';

/**
 * Phone templates (docs/29, owner 15 September 2026): a handset model, its keys and the
 * network its phones share. An extension gets a phone by its MAC and one of these.
 *
 * One screen for both consoles. With an account it lists that account's templates with the
 * platform's beside them, read-only; without one (/inventory/templates) it is the platform's
 * own. The open template is the last part of the address, by its slug.
 *
 * Keys are edited on a drawing of the phone where its model has a layout (docs/29 decision 14,
 * modelled on Yealink's Configuration Generator), and as a list otherwise. Both edit the same
 * draft. A new template shows the drawing as soon as its handset is chosen.
 */

const START_LABEL: Record<TemplateStart, string> = {
  blank: 'Blank', back_office: 'Back office', receptionist: 'Receptionist', agent: 'Agent',
  hot_desking: 'Hot desking',
};

const phones = (n: number): string => `${n} phone${n === 1 ? '' : 's'}`;

/**
 * What a row is used for. An account's template is used by phones. A starting layout is not used
 * by any phone at all -- that is the point of it (0112) -- so it counts the copies made from it
 * and the phones on those, which is what somebody about to edit one needs to know. Those counts
 * pass through row-level security, so only the platform's own list can see all of them.
 */
function useOf(t: ProvisioningTemplate, platform: boolean): ReactNode {
  if (t.tenant_id !== null) return t.phone_count || <span className="arc-subtle">—</span>;
  // An account counts only its own copies, which is not a number worth showing it.
  if (!platform) return <span className="arc-subtle">—</span>;
  if (t.copy_count === 0) return <span className="arc-subtle">not copied</span>;
  return (
    <span title={`${t.copy_count} cop${t.copy_count === 1 ? 'y' : 'ies'} in ${t.copy_tenant_count} account${t.copy_tenant_count === 1 ? '' : 's'}`}>
      {t.copy_phone_count || '—'} <span className="arc-subtle">via {t.copy_count}</span>
    </span>
  );
}

/** The account's extensions, for busy lamps; none for the platform's own templates. */
function useSeats(tenantId: string | null): Seat[] {
  const extensions = useExtensions(tenantId, { limit: 500 });
  return (extensions.data?.data ?? []).map((e) => ({ id: e.id, number: e.number, display_name: e.display_name ?? e.number }));
}

export function TemplatesPage({ tenantId }: { tenantId: string | null }) {
  const route = useSubroute();
  const templates = useTemplates(tenantId);
  const slug = route.rest[0];

  const list = templates.data?.data ?? [];
  // An account's own template before a platform one of the same name.
  const open = slug
    ? list.find((t) => t.slug === slug && t.tenant_id !== null) ?? list.find((t) => t.slug === slug) ?? null
    : null;

  if (slug && open) {
    return (
      <TemplateEditor key={`${open.id}:${open.version}`} tenantId={tenantId} template={open}
        onBack={() => route.go([])} onOpen={(s) => route.go([s])} />
    );
  }

  return (
    <TemplateList tenantId={tenantId} templates={list} loading={templates.isLoading}
      error={templates.error} missing={slug && !templates.isLoading ? slug : null}
      onOpen={(s) => route.go([s])} />
  );
}

function TemplateList({ tenantId, templates, loading, error, missing, onOpen }: {
  tenantId: string | null; templates: ProvisioningTemplate[]; loading: boolean; error: unknown;
  missing: string | null; onOpen: (slug: string) => void;
}) {
  const can = useSession((s) => s.can);
  const writable = can('provisioningtemplate:write');
  const [adding, setAdding] = useState(false);

  return (
    <>
      <SectionHead title="Phone templates"
        sub="A handset model, its keys and its network. Many phones can share one."
        action={writable && (
          <button disabled={adding} type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setAdding((v) => !v)}>
            <Icon name="plus" size={13} />
            New template
          </button>
        )} />

      {error !== null && error !== undefined && <ErrorBanner error={error} />}
      {missing && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>There is no template called {missing}.</span>
        </div>
      )}

      {adding && <NewTemplate tenantId={tenantId} onCancel={() => setAdding(false)} onCreated={(t) => { setAdding(false); onOpen(t.slug); }} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Template</th><th>Handset</th><th className="arc-td-num">Keys</th>
                <th className="arc-td-num" title="Phones using it; for a starting layout, phones on copies of it">
                  Phones
                </th><th>Changed</th>
              </tr>
            </thead>
            {loading ? <TableSkeleton cols={5} /> : (
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id} className="arc-clickable" onClick={() => onOpen(t.slug)}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{t.name}</span>
                      {t.tenant_id === null && <span className="arc-subtle"> · starting layout</span>}
                      {t.extension_number && <span className="arc-subtle"> · {t.extension_number}’s own</span>}
                    </td>
                    <td className="arc-muted">{t.vendor_label} {t.model}</td>
                    <td className="arc-td-num arc-muted">{t.keys.length}</td>
                    <td className="arc-td-num arc-muted">{useOf(t, tenantId === null)}</td>
                    <td className="arc-muted">{relativeTime(t.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!loading && templates.length === 0 && (
          <Empty icon="phone" title="No templates"
            hint="Start one from a layout: back office, receptionist, agent or hot desking." />
        )}
      </div>
    </>
  );
}

/** Offered models by make, for choosing a template's handset. */
function ModelSelect({ id, value, onChange, models, keep }: {
  id: string; value: string; onChange: (v: string) => void; models: HandsetModel[];
  /** A retired model the template is already on stays choosable. */
  keep?: string;
}) {
  const groups = new Map<string, HandsetModel[]>();
  for (const m of models) {
    if (!m.active && m.id !== keep) continue;
    groups.set(m.vendor_label, [...(groups.get(m.vendor_label) ?? []), m]);
  }
  return (
    <select id={id} className="arc-select" value={value} required onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled>Choose a handset…</option>
      {[...groups].map(([make, ms]) => (
        <optgroup key={make} label={make}>
          {ms.map((m) => <option key={m.id} value={m.id}>{m.vendor_label} {m.model}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

/**
 * A new template. Choosing a handset that has a drawing opens it here, on the layout chosen to
 * start from (owner, 16 September 2026), and the template is created with the keys as drawn.
 */
function NewTemplate({ tenantId, onCreated, onCancel }: {
  tenantId: string | null; onCreated: (t: ProvisioningTemplate) => void; onCancel: () => void;
}) {
  const create = useCreateTemplate(tenantId);
  const catalogue = useHandsetCatalogue();
  const seats = useSeats(tenantId);
  const [name, setName] = useState('');
  const [modelId, setModelId] = useState('');
  const [start, setStart] = useState<TemplateStart>('back_office');
  const [keys, setKeys] = useState<KeyMap>({});
  const [sent, setSent] = useState<Array<{ slot: string }>>([]);

  const model = (catalogue.data?.data ?? []).find((m) => m.id === modelId) ?? null;
  const layout = model?.layout ?? null;

  // A new handset or starting layout starts the drawing again from that layout.
  useEffect(() => {
    if (model) setKeys(keysFromPreset(presetKeys(start, 1), model.line_keys, model.xml_browser));
  }, [model, start]);

  const fieldErrors = create.error instanceof ApiClientError ? create.error.fieldErrors : {};
  const errors = errorsBySlot(fieldErrors, sent);

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        const input = layout ? keysToInput(keys) : [];
        setSent(input);
        create.mutate({
          name: name.trim(), handset_model_id: modelId, start_from: start,
          ...(layout ? { keys: input.map((x) => x.key) } : {}),
        }, { onSuccess: onCreated });
      }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-name">Name</label>
          <input id="tpl-name" className="arc-input" value={name} maxLength={60} required
            placeholder="Reception" onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-model">Handset</label>
          <ModelSelect id="tpl-model" value={modelId} onChange={setModelId} models={catalogue.data?.data ?? []} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-start">Start from</label>
          <select id="tpl-start" className="arc-select" value={start}
            onChange={(e) => setStart(e.target.value as TemplateStart)}>
            {TEMPLATE_STARTS.map((s) => <option key={s} value={s}>{START_LABEL[s]}</option>)}
          </select>
        </div>
      </div>

      {model && layout && (
        <div style={{ marginTop: 16 }}>
          <PhoneKeysEditor model={model.model} vendor={model.vendor_label} layout={layout} keys={keys} onChange={setKeys}
            writable xmlBrowser={model.xml_browser} allowNamed={tenantId !== null} seats={seats}
            errors={errors} />
          <KeySettings tenantId={tenantId} modelId={model.id} keys={keys} />
        </div>
      )}
      {model && !layout && (
        <p className="arc-hint" style={{ marginTop: 10 }}>
          No drawing for the {model.model} yet. Its keys are set as a list once it is created.
        </p>
      )}

      <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
        <div className="arc-spacer" />
        <button type="submit" className="arc-btn arc-btn-primary"
          disabled={create.isPending || !name.trim() || !modelId}>
          {create.isPending ? 'Creating…' : 'Create template'}
        </button>
        <button type="button" className="arc-btn" style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/** "South Africa (Johannesburg) · UTC+2". */
function zoneLabel(z: PhoneTimeZone): string {
  const abs = Math.abs(z.offset_minutes);
  const m = abs % 60;
  const utc = z.offset_minutes === 0 ? 'UTC'
    : `UTC${z.offset_minutes < 0 ? '−' : '+'}${Math.floor(abs / 60)}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
  return z.id === 'UTC' ? 'UTC' : `${z.label} · ${utc}`;
}

interface Draft {
  name: string;
  notes: string;
  network_mode: 'unmanaged' | 'dhcp';
  vlan: string;
  /** '' for the account's own zone. */
  time_zone: string;
  daylight_saving: boolean;
  /** A new admin password; '' keeps what is set. */
  admin_password: string;
  /** Back to the vendor's default (admin). */
  reset_admin_password: boolean;
  keys: KeyMap;
}

const draftOf = (t: ProvisioningTemplate): Draft => ({
  name: t.name,
  notes: t.notes ?? '',
  network_mode: t.network_mode,
  vlan: t.vlan_id ? String(t.vlan_id) : '',
  time_zone: t.time_zone ?? '',
  daylight_saving: t.daylight_saving,
  admin_password: '',
  reset_admin_password: false,
  keys: keysFromTemplate(t.keys),
});

function TemplateEditor({ tenantId, template, onBack, onOpen }: {
  tenantId: string | null; template: ProvisioningTemplate;
  onBack: () => void; onOpen: (slug: string) => void;
}) {
  const can = useSession((s) => s.can);
  const save = useSaveTemplate(tenantId);
  const del = useDeleteTemplate(tenantId);
  const catalogue = useHandsetCatalogue();
  const seats = useSeats(tenantId);
  const initial = useMemo(() => draftOf(template), [template]);
  const [draft, setDraft] = useState<Draft>(initial);
  const [extra, setExtra] = useState(0);
  const [duplicating, setDuplicating] = useState(false);
  const [view, setView] = useState<'phone' | 'list'>('phone');
  const [sent, setSent] = useState<Array<{ slot: string }>>([]);
  useEffect(() => { setDraft(initial); }, [initial]);

  const model = (catalogue.data?.data ?? []).find((m) => m.id === template.handset_model_id) ?? null;
  const layout = model?.layout ?? null;
  const shown = layout ? view : 'list';

  /**
   * A platform template is a blueprint: it is copied into an account and the copy is what phones
   * use, so editing it reaches nobody's handset (0112, owner 25 September 2026). An account can
   * read it and copy it, never change it.
   */
  const blueprint = template.tenant_id === null;
  const shared = tenantId !== null && blueprint;
  const writable = can('provisioningtemplate:write') && !shared;
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const fieldErrors = save.error instanceof ApiClientError ? save.error.fieldErrors : {};
  const errors = errorsBySlot(fieldErrors, sent);

  const setKeys = (keys: KeyMap) => setDraft((d) => ({ ...d, keys }));
  const networkChanged = draft.network_mode !== template.network_mode
    || (draft.vlan.trim() ? Number(draft.vlan) : null) !== template.vlan_id;

  const submit = async () => {
    if (template.phone_count > 0) {
      const warning = networkChanged ? ' A wrong VLAN takes them offline.' : '';
      if (!(await confirmAction({
        title: `Update ${phones(template.phone_count)}?`,
        body: `They change at their next fetch.${warning}`,
        action: 'Update phones',
      }))) return;
    }
    const input = keysToInput(draft.keys);
    setSent(input);
    save.mutate({
      id: template.id,
      version: template.version,
      expected_phone_count: template.phone_count,
      confirm_network: networkChanged,
      name: draft.name.trim(),
      notes: draft.notes.trim() || null,
      network_mode: draft.network_mode,
      vlan_id: draft.vlan.trim() ? Number(draft.vlan) : null,
      time_zone: draft.time_zone || null,
      daylight_saving: draft.daylight_saving,
      ...(draft.admin_password ? { admin_password: draft.admin_password }
        : draft.reset_admin_password ? { admin_password: null } : {}),
      keys: input.map((x) => x.key),
    });
  };

  return (
    <>
      <button type="button" className="arc-crumb" onClick={onBack}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <Icon name="arrowLeft" size={15} /> Phone templates
      </button>

      <SectionHead title={template.name}
        sub={<>
            {template.vendor_label} {template.model} · version {template.version} ·{' '}
            {shared ? 'a starting layout · copy it to use it here'
              : blueprint
                ? template.copy_count === 0 ? 'a starting layout, not copied yet'
                  : `a starting layout · ${template.copy_count} cop${template.copy_count === 1 ? 'y' : 'ies'} in ${template.copy_tenant_count} account${template.copy_tenant_count === 1 ? '' : 's'}, ${phones(template.copy_phone_count)}`
                : template.phone_count ? `used by ${phones(template.phone_count)}` : 'no phones yet'}
            {template.extension_number ? ` · ${template.extension_number}’s own` : ''}
          </>}
        action={
        <div className="arc-row" style={{ gap: 6 }}>
          {can('provisioningtemplate:write') && (
            <button disabled={duplicating} type="button"
              className={`arc-btn arc-btn-sm${shared ? ' arc-btn-primary' : ''}`}
              onClick={() => setDuplicating((v) => !v)}>
              {shared ? 'Use this' : 'Duplicate'}
            </button>
          )}
          {writable && template.phone_count === 0 && (
            <RowAction kind="delete" label={`Delete ${template.name}`} disabled={del.isPending}
              onClick={() => {
                void confirmAction({ title: `Delete ${template.name}?`, action: 'Delete', danger: true })
                  .then((ok) => { if (ok) del.mutate(template.id, { onSuccess: onBack }); });
              }} />
          )}
        </div>} />

      {duplicating && <DuplicateTemplate tenantId={tenantId} template={template} onOpen={onOpen} onCancel={() => setDuplicating(false)} />}

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="tpl-ed-name">Name</label>
            <input id="tpl-ed-name" className="arc-input" value={draft.name} maxLength={60} disabled={!writable}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <span className="arc-label">Network</span>
            <Segmented label="Network" value={draft.network_mode}
              options={[['unmanaged', 'As the phone has it'], ['dhcp', 'DHCP']]}
              onChange={(mode) => { if (writable) setDraft((d) => ({ ...d, network_mode: mode })); }} />
          </div>
          <div className="arc-field" style={{ margin: 0, maxWidth: 160 }}>
            <label className="arc-label" htmlFor="tpl-ed-vlan">Voice VLAN</label>
            <input id="tpl-ed-vlan" className="arc-input arc-mono" value={draft.vlan} inputMode="numeric"
              placeholder="none" disabled={!writable}
              onChange={(e) => setDraft((d) => ({ ...d, vlan: e.target.value.replace(/\D/g, '').slice(0, 4) }))} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="tpl-ed-tz">Time zone</label>
            <select id="tpl-ed-tz" className="arc-select" value={draft.time_zone} disabled={!writable}
              onChange={(e) => setDraft((d) => ({ ...d, time_zone: e.target.value }))}>
              <option value="">Account’s time zone</option>
              {PHONE_TIME_ZONES.map((z) => <option key={z.id} value={z.id}>{zoneLabel(z)}</option>)}
            </select>
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="tpl-ed-dst">Daylight saving</label>
            <Switch id="tpl-ed-dst" checked={draft.daylight_saving} disabled={!writable}
              onChange={(on) => setDraft((d) => ({ ...d, daylight_saving: on }))} />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="tpl-ed-adminpw">Phone admin password</label>
            <div className="arc-row" style={{ gap: 6 }}>
              <input id="tpl-ed-adminpw" className="arc-input arc-mono" type="password" autoComplete="new-password"
                value={draft.admin_password} maxLength={32} disabled={!writable}
                placeholder={template.admin_password_set && !draft.reset_admin_password ? 'Set · unchanged' : 'admin (default)'}
                onChange={(e) => setDraft((d) => ({ ...d, admin_password: e.target.value, reset_admin_password: false }))} />
              {writable && template.admin_password_set && !draft.reset_admin_password && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => setDraft((d) => ({ ...d, admin_password: '', reset_admin_password: true }))}>
                  Reset
                </button>
              )}
            </div>
            {fieldErrors['admin_password'] && <div className="arc-error">{fieldErrors['admin_password']}</div>}
          </div>
        </div>
        <p className="arc-hint" style={{ marginTop: 8 }}>
          A phone with its own static address keeps its own network settings.
        </p>
      </div>

      <SubHead action={layout ? (
        <Segmented label="Keys as" value={view} onChange={setView}
          options={[['phone', 'Phone'], ['list', 'List']]} />
      ) : null}>Keys</SubHead>

      {shown === 'phone' && layout && model ? (
        <>
          <div className="arc-card arc-card-body">
            <PhoneKeysEditor model={template.model} vendor={template.vendor_label} layout={layout} keys={draft.keys} onChange={setKeys}
              writable={writable} xmlBrowser={template.xml_browser}
              allowNamed={template.tenant_id !== null} seats={seats} errors={errors} />
          </div>
          <KeySettings tenantId={tenantId} modelId={template.handset_model_id} keys={draft.keys} />
        </>
      ) : (
        <KeyList template={template} keys={draft.keys} onChange={setKeys} writable={writable}
          seats={seats} errors={errors} extra={extra} onMore={() => setExtra((n) => n + 5)} />
      )}

      {writable && (
        <div style={{ marginTop: 14 }}>
          <FormActions dirty={dirty} saving={save.isPending} onSave={() => void submit()} onReset={() => setDraft(initial)} />
        </div>
      )}
    </>
  );
}

/** The line keys as a numbered list: for a model with no drawing, or for anyone who prefers it. */
function KeyList({ template, keys, onChange, writable, seats, errors, extra, onMore }: {
  template: ProvisioningTemplate; keys: KeyMap; onChange: (next: KeyMap) => void;
  writable: boolean; seats: Seat[]; errors: Readonly<Record<string, string>>;
  extra: number; onMore: () => void;
}) {
  const used = Object.entries(keys)
    .filter(([slot, k]) => slot.startsWith('line:') && k.kind !== '')
    .map(([slot]) => Number(slot.split(':')[1]));
  const rows = template.line_keys ?? Math.min(120, Math.max(10, ...used) + extra);
  const setKey = (position: number, next: Partial<KeyDraft>) => {
    const slot = slotOf('line', position);
    onChange({ ...keys, [slot]: { ...(keys[slot] ?? EMPTY_KEY), ...next } });
  };
  // What a line key can be here: the phone's own functions only on a model that is drawn.
  const kinds = (Object.keys(KIND_LABEL) as TemplateKeyKind[])
    .filter((kind) => kind !== 'none' && kind !== 'phone')
    // A template for every account cannot name somebody.
    .filter((kind) => kind !== 'blf' || template.tenant_id !== null)
    // Log in and log out open a screen, which this model cannot.
    .filter((kind) => template.xml_browser || (kind !== 'desk_login' && kind !== 'desk_logout'));

  return (
    <div className="arc-card">
      <div className="arc-table-wrap">
        <table className="arc-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Key</th><th style={{ width: 170 }}>Does</th><th>Target</th><th>Label</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, i) => i + 1).map((position) => {
              const k = keys[slotOf('line', position)] ?? EMPTY_KEY;
              const error = errors[slotOf('line', position)];
              return (
                <tr key={position}>
                  <td><span className="arc-key-chip" style={{ width: 24, height: 24, fontSize: 11.5 }}>{position}</span></td>
                  <td>
                    <select className="arc-select" value={k.kind} disabled={!writable}
                      aria-label={`Key ${position} does`}
                      onChange={(e) => setKey(position, { ...EMPTY_KEY, kind: e.target.value as TemplateKeyKind | '' })}>
                      <option value="">Nothing</option>
                      {kinds.map((kind) => <option key={kind} value={kind}>{KIND_LABEL[kind]}</option>)}
                      {k.kind !== '' && !(kinds as readonly TemplateKeyKind[]).includes(k.kind)
                        && <option value={k.kind}>{KIND_LABEL[k.kind]}</option>}
                    </select>
                  </td>
                  <td>
                    {/* The phone's own extension is its only line (owner, 15 September 2026);
                        anybody else is a busy lamp. */}
                    {k.kind === 'line' && <span className="arc-muted">The phone’s extension</span>}
                    {k.kind === 'blf' && (
                      <select className="arc-select" value={k.extension_id ?? ''} disabled={!writable}
                        aria-label={`Key ${position} extension`}
                        onChange={(e) => setKey(position, { extension_id: e.target.value || null })}>
                        <option value="" disabled>Choose an extension…</option>
                        {seats.map((s) => <option key={s.id} value={s.id}>{s.number} · {s.display_name}</option>)}
                      </select>
                    )}
                    {k.kind === 'speed_dial' && (
                      <input className="arc-input arc-mono" value={k.value} disabled={!writable}
                        inputMode="tel" placeholder="0115550100" aria-label={`Key ${position} number`}
                        onChange={(e) => setKey(position, { value: e.target.value.replace(/[^0-9*#+]/g, '').slice(0, 32) })} />
                    )}
                    {k.kind === 'feature' && (
                      <select className="arc-select" value={k.value} disabled={!writable}
                        aria-label={`Key ${position} feature`}
                        onChange={(e) => setKey(position, { value: e.target.value })}>
                        <option value="" disabled>Choose a feature…</option>
                        {TEMPLATE_KEY_FEATURES.map((f) => <option key={f} value={f}>{FEATURE_LABEL[f]}</option>)}
                      </select>
                    )}
                    {k.kind === 'phone' && <span className="arc-muted">{derivedLabel(k, seats)}</span>}
                    {(k.kind === '' || k.kind === 'blf_next' || k.kind === 'desk_login' || k.kind === 'desk_logout') && (
                      <span className="arc-subtle">—</span>
                    )}
                    {error && <div className="arc-error">{error}</div>}
                  </td>
                  <td>
                    {k.kind !== '' && (
                      <input className="arc-input" value={k.label} maxLength={32} disabled={!writable}
                        placeholder={derivedLabel(k, seats)} aria-label={`Key ${position} label`}
                        onChange={(e) => setKey(position, { label: e.target.value })} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {template.line_keys === null && writable && rows < 120 && (
        <div className="arc-card-body" style={{ paddingTop: 10 }}>
          <button type="button" className="arc-btn arc-btn-sm" onClick={onMore}>
            <Icon name="plus" size={12} /> More keys
          </button>
          <span className="arc-subtle" style={{ fontSize: 11.5, marginLeft: 10 }}>
            The catalogue does not record how many keys a {template.model} has.
          </span>
        </div>
      )}
    </div>
  );
}

function DuplicateTemplate({ tenantId, template, onOpen, onCancel }: {
  tenantId: string | null; template: ProvisioningTemplate; onCancel: () => void; onOpen: (slug: string) => void;
}) {
  const dup = useDuplicateTemplate(tenantId);
  const catalogue = useHandsetCatalogue();
  const [name, setName] = useState(`${template.name} copy`.slice(0, 60));
  const [modelId, setModelId] = useState(template.handset_model_id);
  const [done, setDone] = useState<DuplicatedTemplate | null>(null);

  if (done) {
    const own = done.dropped_fixed ?? 0;
    return (
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-row" style={{ gap: 8 }}>
          <span>
            Created {done.template.name}.
            {done.dropped.length > 0 && ` Key${done.dropped.length === 1 ? '' : 's'} ${done.dropped.join(', ')} did not fit a ${done.template.model} and ${done.dropped.length === 1 ? 'was' : 'were'} left out.`}
            {own > 0 && ` ${own} of the phone’s own keys ${own === 1 ? 'is' : 'are'} not on a ${done.template.model} and ${own === 1 ? 'was' : 'were'} left out.`}
          </span>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => onOpen(done.template.slug)}>
            Open
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        dup.mutate({ id: template.id, name: name.trim(), handset_model_id: modelId }, { onSuccess: setDone });
      }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-dup-name">Name</label>
          <input id="tpl-dup-name" className="arc-input" value={name} maxLength={60} required
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-dup-model">Handset</label>
          <ModelSelect id="tpl-dup-model" value={modelId} onChange={setModelId}
            models={catalogue.data?.data ?? []} keep={template.handset_model_id} />
        </div>
      </div>
      <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
        <span className="arc-subtle" style={{ fontSize: 11.5 }}>
          {tenantId && template.tenant_id === null
            ? 'The copy is this account’s own: its phones use the copy, and the starting layout can change behind them without touching them.'
            : 'Keys another handset does not have are left out.'}
        </span>
        <div className="arc-spacer" />
        <button type="submit" className="arc-btn arc-btn-primary" disabled={dup.isPending || !name.trim() || !modelId}>
          {dup.isPending ? 'Copying…' : tenantId && template.tenant_id === null ? 'Copy to this account' : 'Duplicate'}
        </button>
        <button type="button" className="arc-btn" style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
